/**
 * bakeKind — pure helpers for the separated lighting/navmesh bake (ENG-592).
 *
 * Extracted from bakeLightingApi.js / LightingStatusChip.jsx so the decision
 * logic is unit-testable without the Vite (`import.meta.env`) + fetch coupling
 * those modules carry. No imports on purpose — keep it dependency-free.
 */

/**
 * bakeKindFlags maps a bake `kind` to the {lighting, navmesh} selection the
 * bake endpoint expects. The two builds are independent: Build Lighting must
 * NOT re-run the navmesh carve, and Build Navmesh must NOT re-run the slow
 * reflection captures.
 *   'lighting' -> { lighting: true,  navmesh: false }
 *   'navmesh'  -> { lighting: false, navmesh: true  }
 * Anything else is treated as "both" (back-compat with the old combined bake).
 */
export function bakeKindFlags(kind) {
  if (kind === 'lighting') return { lighting: true, navmesh: false }
  if (kind === 'navmesh') return { lighting: false, navmesh: true }
  return { lighting: true, navmesh: true }
}

/**
 * rowBakeState decides what a single status-chip row (Lighting or Navmesh)
 * should display, given the shared bake state. The key rule (ENG-592): only the
 * row whose `kind` matches the in-flight bake spins — the other row keeps
 * showing its own ready/needs state. A bake with no recorded kind (older state
 * or a combined back-compat bake) spins both rows.
 *
 * Returns one of: 'unsupported' | 'baking' | 'error' | 'ready' | 'needs'.
 */
export function rowBakeState({ bakeable, baking, bakingKind, status, ready, row }) {
  // A loaded artifact outranks the can't-REbake label. `bakeable` only says
  // whether the bake pipeline could re-run on this file (it parses INRIA .ply
  // only) — it says nothing about the lighting already applied. An environment
  // whose file was replaced in place (ENG: PLY re-encoded as SPZ) keeps all its
  // baked artifacts, and showing those as ".ply only" reads as "no lighting"
  // while 180 probes are visibly lighting the avatar. Unsupported now means
  // what it says: nothing loaded AND nothing this file could bake.
  if (!bakeable) return ready ? 'ready' : 'unsupported'
  if (baking && (bakingKind == null || bakingKind === row)) return 'baking'
  if (status === 'error') return 'error'
  return ready ? 'ready' : 'needs'
}

/**
 * bakePhaseLabel formats the staged-progress label shown next to the % while a
 * bake is in flight (ENG-592): "Downloading (1/2)". The "(n/m)" suffix only
 * appears for a genuinely multi-stage bake; a single-stage or unknown phase
 * shows just the label (or empty string if there's no phase yet).
 */
export function bakePhaseLabel({ phase, stage, stages } = {}) {
  if (!phase) return ''
  if (stages > 1 && stage > 0) return `${phase} (${stage}/${stages})`
  return phase
}
